/**
 * Loads recent videos from a YouTube channel into [data-ms-youtube-root] grids.
 * Requires API key + channel identifier in data attributes (see section markup).
 */
(function () {
  const SELECTOR = '[data-ms-youtube-root]';

  function parseChannelId(raw) {
    const s = (raw || '').trim();
    if (!s) return null;
    if (/^UC[\w-]{22}$/.test(s)) return s;
    const fromPath = s.match(/youtube\.com\/channel\/(UC[\w-]{22})/i);
    if (fromPath) return fromPath[1];
    return null;
  }

  function parseHandle(raw) {
    const s = (raw || '').trim();
    if (!s) return null;
    const fromUrl = s.match(/youtube\.com\/@([^/?#]+)/i);
    if (fromUrl) return decodeURIComponent(fromUrl[1]);
    if (s.startsWith('@')) return s.slice(1).trim() || null;
    return null;
  }

  async function resolveChannelId(input, apiKey) {
    const direct = parseChannelId(input);
    if (direct) return direct;

    const handle = parseHandle(input);
    if (!handle) return null;

    const url = new URL('https://www.googleapis.com/youtube/v3/channels');
    url.searchParams.set('part', 'id');
    url.searchParams.set('forHandle', handle);
    url.searchParams.set('key', apiKey);

    const res = await fetch(url.toString());
    const data = await res.json();
    if (data.error) throw new Error(data.error.message || 'Channel lookup failed');
    const id = data.items && data.items[0] && data.items[0].id;
    return id || null;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  }

  function cardHtml(videoId, title, publishedAt, thumbUrl) {
    const href = 'https://www.youtube.com/watch?v=' + encodeURIComponent(videoId);
    const dateLine = formatDate(publishedAt);
    const safeTitle = escapeHtml(title || 'Video');
    const safeAlt = escapeHtml(title || 'Video');

    return (
      '<article class="ms-card ms-card--journal ms-card--youtube">' +
      '<a href="' +
      href +
      '" class="ms-card__media-link ms-youtube-feed__thumb-link" target="_blank" rel="noopener noreferrer">' +
      '<img src="' +
      thumbUrl +
      '" alt="' +
      safeAlt +
      '" loading="lazy" width="640" height="360" decoding="async">' +
      '<span class="ms-youtube-feed__play" aria-hidden="true">' +
      '<svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">' +
      '<circle cx="28" cy="28" r="28" fill="rgba(0,0,0,0.55)"/>' +
      '<path d="M23 18L38 28L23 38V18Z" fill="white"/>' +
      '</svg></span></a>' +
      '<div class="ms-card__body">' +
      (dateLine
        ? '<p class="ms-eyebrow" style="margin-bottom: var(--ms-space-2);">' +
          dateLine +
          '</p>'
        : '') +
      '<h3 class="ms-card__title" style="font-family: var(--font-heading-family); font-size: var(--ms-text-lg); margin: 0;">' +
      '<a href="' +
      href +
      '" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: none;">' +
      safeTitle +
      '</a></h3></div></article>'
    );
  }

  async function loadIntoRoot(root) {
    const grid = root.querySelector('[data-youtube-grid]');
    if (!grid) return;

    const apiKey = (root.dataset.apiKey || '').trim();
    const channelInput = (root.dataset.channelInput || '').trim();
    const limit = Math.min(12, Math.max(1, parseInt(root.dataset.limit || '6', 10) || 6));

    if (!apiKey || !channelInput) return;

    root.classList.add('ms-youtube-feed--loading');

    try {
      const channelId = await resolveChannelId(channelInput, apiKey);
      if (!channelId) {
        grid.innerHTML =
          '<p class="ms-rte">Could not determine a channel ID. Paste a UC… ID, a full channel URL, or an @handle URL.</p>';
        return;
      }

      const searchUrl = new URL('https://www.googleapis.com/youtube/v3/search');
      searchUrl.searchParams.set('part', 'snippet');
      searchUrl.searchParams.set('channelId', channelId);
      searchUrl.searchParams.set('maxResults', String(limit));
      searchUrl.searchParams.set('order', 'date');
      searchUrl.searchParams.set('type', 'video');
      searchUrl.searchParams.set('key', apiKey);

      const res = await fetch(searchUrl.toString());
      const data = await res.json();

      if (data.error) {
        throw new Error(data.error.message || 'YouTube API error');
      }

      const items = data.items || [];
      if (!items.length) {
        grid.innerHTML = '<p class="ms-rte">No public videos found for this channel yet.</p>';
        return;
      }

      const html = items
        .map(function (item) {
          const vid = item.id && item.id.videoId;
          if (!vid) return '';
          const sn = item.snippet || {};
          const thumbs = sn.thumbnails || {};
          const thumbUrl =
            (thumbs.medium && thumbs.medium.url) ||
            (thumbs.high && thumbs.high.url) ||
            (thumbs.default && thumbs.default.url) ||
            'https://i.ytimg.com/vi/' + vid + '/hqdefault.jpg';
          return cardHtml(vid, sn.title || 'Video', sn.publishedAt, thumbUrl);
        })
        .join('');

      grid.innerHTML = html;
    } catch (e) {
      grid.innerHTML =
        '<p class="ms-rte">Videos could not be loaded. Check the API key, channel, and quota in Google Cloud Console.</p>';
    } finally {
      root.classList.remove('ms-youtube-feed--loading');
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(function (root) {
      loadIntoRoot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || typeof event.target.querySelector !== 'function') return;
    event.target.querySelectorAll(SELECTOR).forEach(function (root) {
      loadIntoRoot(root);
    });
  });
})();
